import React from 'react';
import { useParams } from 'react-router-dom';
import { getFrameworkBySlug } from '../data/unifiedEcosystem';
import { ExternalLink, BookOpen } from 'lucide-react';

export const FrameworkDetailPage = () => {
  const { slug } = useParams();
  const framework = getFrameworkBySlug(slug);

  if (!framework) {
    return (
      <div className="editorial-container py-32 text-center">
        <p className="text-muted-foreground text-sm uppercase tracking-widest">Framework not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="editorial-container py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div className="relative aspect-[4/5] bg-black">
            <img
              src={framework.image}
              alt={framework.title}
              className="w-full h-full object-cover opacity-90"
            />
          </div>

          <div className="flex flex-col justify-center space-y-8">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest accent-red">
                Framework
              </span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl editorial-serif leading-none">
              {framework.title}
            </h1>
            
            <p className="text-xl text-foreground/80 leading-relaxed">
              {framework.tagline}
            </p>

            <p className="text-base text-foreground/70 leading-relaxed">
              {framework.description}
            </p>

            <div className="text-sm text-muted-foreground uppercase tracking-wider">
              By {framework.author}
            </div>
          </div>
        </div>
      </section>

      {/* Endorsement */}
      {framework.endorsement && (
        <section className="editorial-container py-16 border-t border-border">
          <div className="editorial-narrow">
            <div className="border-l-2 border-primary pl-8">
              <p className="text-xl lg:text-2xl editorial-serif leading-relaxed mb-6">
                "{framework.endorsement.quote}"
              </p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {framework.endorsement.author}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Components */}
      <section className="editorial-container py-16 border-t border-border">
        <div className="space-y-8">
          <h2 className="text-2xl editorial-serif">Framework Components</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {framework.components.map((component, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <BookOpen size={16} className="accent-red mt-1 flex-shrink-0" />
                <span className="text-sm text-foreground/80">{component}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="editorial-container py-16 border-t border-border">
        <div className="space-y-8">
          <h2 className="text-2xl editorial-serif">Available Now</h2>
          <div className="space-y-6">
            {framework.offerings.map((offering, idx) => (
              <div key={idx} className="border-l-2 border-border pl-6">
                <h3 className="text-lg font-semibold mb-2">{offering.name}</h3>
                {offering.description && (
                  <p className="text-sm text-foreground/70 mb-4">{offering.description}</p>
                )}
                {offering.link && (
                  <a
                    href={offering.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-border hover:border-primary text-sm uppercase tracking-widest"
                  >
                    Get {offering.name}
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FrameworkDetailPage;
